import { createContext, useContext, useEffect, useState } from "react";
import { DEFAULT_LANG, DICTS } from "@/lib/locales";

// The visitor's language is remembered per device. Spanish is the intentional
// first-visit default for this Puerto Rico-first local guide.
const STORE_KEY = "yts:lang";
// Their own pick — set once from the welcome card. Until it exists the site
// starts in Spanish on a new device, but greets every visitor with the choice.
const CHOICE_KEY = "yts:lang:chosen";
const LangContext = createContext(null);

const readChosen = () => {
  try {
    return window.localStorage.getItem(CHOICE_KEY) === "1";
  } catch {
    return false;
  }
};

const readLang = () => {
  try {
    const saved = window.localStorage.getItem(STORE_KEY);
    if (saved && DICTS[saved]) return saved;
  } catch {
    // storage unavailable — fall through to the Spanish-first default
  }
  return DEFAULT_LANG;
};

export function LangProvider({ children }) {
  const [lang, setLang] = useState(readLang);
  const [chosen, setChosen] = useState(readChosen);

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      window.localStorage.setItem(STORE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  // A language the visitor picked themselves closes the welcome card for good.
  const chooseLang = (code) => {
    if (!DICTS[code]) return;
    setLang(code);
    setChosen(true);
    try {
      window.localStorage.setItem(STORE_KEY, code);
      window.localStorage.setItem(CHOICE_KEY, "1");
    } catch {
      // ignore
    }
  };

  return (
    <LangContext.Provider value={{ lang, setLang, chooseLang, needsChoice: !chosen }}>
      {children}
    </LangContext.Provider>
  );
}

/** Translator for the active language, with {placeholder} interpolation. */
export const useT = () => {
  const { lang } = useContext(LangContext) || {};
  const active = lang && DICTS[lang] ? lang : DEFAULT_LANG;
  return (key, vars) => {
    const raw = DICTS[active][key] ?? DICTS[DEFAULT_LANG][key] ?? key;
    if (!vars) return raw;
    return raw.replace(/\{(\w+)\}/g, (match, name) => (vars[name] ?? match));
  };
};

export const useLang = () => {
  const ctx =
    useContext(LangContext) || { lang: DEFAULT_LANG, setLang: () => {}, chooseLang: () => {}, needsChoice: false };
  return { ...ctx, t: useT() };
};